import { useEffect, useId, useState } from "react"
import {
  Alert,
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material"
import {
  BOT_MONITOR_STATUSES,
  BOT_MONITOR_STATUS_LABELS,
} from "../../constants/botMonitor"
import type { BotMonitorFilters as Filters } from "../../interfaces/botMonitor"
import {
  areBotMonitorFiltersEquivalent,
  validateBotMonitorFilters,
} from "../../utils/botMonitorQuery"

interface Props {
  applied: Filters
  resetKey: number
  onApply: (filters: Filters) => unknown
}

export const BotMonitorFilters = ({ applied, resetKey, onApply }: Props) => {
  const [draft, setDraft] = useState(applied)
  const id = useId()
  // Only filter values or explicit history/reset reset the draft, not sort/offset.
  const appliedKey = JSON.stringify(applied)
  useEffect(() => {
    setDraft(JSON.parse(appliedKey) as Filters)
  }, [appliedKey, resetKey])
  const checked = validateBotMonitorFilters(draft)
  const canApply =
    checked.ok && !areBotMonitorFiltersEquivalent(checked.filters, applied)
  const errors = checked.ok ? {} : checked.errors
  return (
    <Stack
      component="form"
      aria-label="Фильтры мониторинга"
      spacing={1}
      onSubmit={(event) => {
        event.preventDefault()
        if (!canApply || !checked.ok) return
        const normalized = checked.filters
        onApply(normalized)
        setDraft(normalized)
      }}
    >
      <TextField
        id={`${id}-search`}
        label="Поиск username"
        value={draft.search}
        onChange={(event) => setDraft({ ...draft, search: event.target.value })}
        error={!!errors.search}
        helperText={
          errors.search ??
          "Буквальная подстрока без учёта регистра; до 100 символов после одного @"
        }
        FormHelperTextProps={{ role: errors.search ? "alert" : undefined }}
      />
      <FormGroup
        row
        role="group"
        aria-label="Статусы"
        aria-describedby={errors.status ? `${id}-status-error` : undefined}
      >
        {BOT_MONITOR_STATUSES.map((status) => (
          <FormControlLabel
            key={status}
            label={BOT_MONITOR_STATUS_LABELS[status]}
            control={
              <Checkbox
                checked={draft.status.includes(status)}
                onChange={(_, checked) =>
                  setDraft({
                    ...draft,
                    status: BOT_MONITOR_STATUSES.filter((value) =>
                      value === status ? checked : draft.status.includes(value),
                    ),
                  })
                }
              />
            }
          />
        ))}
      </FormGroup>
      {errors.status && (
        <Alert id={`${id}-status-error`} severity="error">
          {errors.status}
        </Alert>
      )}
      <FormControlLabel
        label="Только архив"
        control={
          <Switch
            checked={draft.archived}
            onChange={(_, archived) => setDraft({ ...draft, archived })}
          />
        }
      />
      {draft.archived && (
        <Typography variant="body2">
          Только архивные записи; они не проверяются
        </Typography>
      )}
      <Button
        type="submit"
        disabled={!canApply}
        variant="contained"
        sx={{ alignSelf: "flex-start" }}
      >
        Применить фильтры
      </Button>
    </Stack>
  )
}
